import React, { useEffect, useRef, memo } from 'react';
import * as THREE from 'three';

export const LoadingAnimation3D = memo(function LoadingAnimation3D({ isPKI = false }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Capture ref for cleanup
    const container = containerRef.current;

    // Determine size based on screen width (same as PlayButton3D)
    const getSize = () => {
      const width = window.innerWidth;
      if (width < 640) return 280; // mobile (sm)
      if (width < 1024) return 280; // tablet (md, lg)
      return 400; // desktop (xl+)
    };

    const size = getSize();

    // Scene setup
    const scene = new THREE.Scene();

    // Camera
    const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
    camera.position.z = 5;

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setSize(size, size);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const directionalLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight1.position.set(2, 2, 3);
    scene.add(directionalLight1);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    directionalLight2.position.set(-2, -2, -1);
    scene.add(directionalLight2);

    // Create loading ring (similar to play button ring)
    const ringGeometry = new THREE.TorusGeometry(1.5, 0.15, 16, 100);
    const emissiveColor = isPKI ? 0xec4899 : 0x3b82f6; // Pink for PKI, Blue for KI
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: emissiveColor,
      metalness: 0.3,
      roughness: 0.4,
      emissive: emissiveColor,
      emissiveIntensity: 0.6
    });
    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    scene.add(ring);

    // Create pulsing dots around the ring
    const dotGroup = new THREE.Group();
    const dotGeometry = new THREE.SphereGeometry(0.15, 32, 32);
    const dotMaterial = new THREE.MeshStandardMaterial({
      color: emissiveColor,
      metalness: 0.3,
      roughness: 0.4,
      emissive: emissiveColor,
      emissiveIntensity: 0.9
    });

    const numDots = 8;
    const dots = [];
    const ringRadius = 2.0; // Außerhalb des Rings (Ring hat Radius 1.5)
    for (let i = 0; i < numDots; i++) {
      const dot = new THREE.Mesh(dotGeometry, dotMaterial.clone());
      const angle = (i / numDots) * Math.PI * 2;
      dot.position.set(
        Math.cos(angle) * ringRadius,
        Math.sin(angle) * ringRadius,
        0.3 // Move dots forward so they're more visible
      );
      dot.userData.angle = angle;
      dots.push(dot);
      dotGroup.add(dot);
    }
    scene.add(dotGroup);

    // Animation loop
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      const time = Date.now() * 0.001;

      // Rotate ring
      ring.rotation.z = time * 0.5;

      // Gentle floating
      ring.position.y = Math.sin(time * 0.5) * 0.05;
      dotGroup.position.y = Math.sin(time * 0.5) * 0.05;

      // Pulsing glow
      const pulse = Math.sin(time * 2) * 0.2 + 0.7;
      ringMaterial.emissiveIntensity = pulse;

      // Animate dots - wave effect
      dots.forEach((dot, i) => {
        const offset = (i / numDots) * Math.PI * 2;
        const scale = Math.sin(time * 2 + offset) * 0.3 + 1;
        dot.scale.set(scale, scale, scale);

        const intensity = Math.sin(time * 2 + offset) * 0.3 + 0.7;
        dot.material.emissiveIntensity = intensity;
      });

      renderer.render(scene, camera);
    };
    animate();

    // Cleanup - properly dispose THREE.js resources
    return () => {
      cancelAnimationFrame(animationId);

      // Dispose all geometries and materials
      scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });

      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [isPKI]);

  return (
    <div
      ref={containerRef}
      className="w-[280px] h-[280px] sm:w-[280px] sm:h-[280px] lg:w-[400px] lg:h-[400px]"
      style={{
        pointerEvents: 'none'
      }}
    />
  );
});
